'use client';

import { useTranslation } from 'react-i18next';
import { Button } from '@/components/buttons';
import { isGamePage } from '@/helpers/game';
import { useToggle } from '@/hooks/common';
import type { FaqItem, SeoContentResult } from '@/modules/seo/content/shared';
import { useSeoContent } from '@/modules/seo/content/useSeoContent';
import { FaqSchema } from '@/modules/server/components/FaqSchema';
import { ScContainer, ScStaticPage } from '@/styles/common';
import { ClientSeoFaq } from './ClientSeoFaq';
import {
  ScDescriptionAction,
  ScDescriptionContainer,
  ScDescriptionText,
  ScSeoTopSpacing,
} from './styled';

interface SeoDescriptionViewProps {
  content?: string;
  faqItems: FaqItem[] | null;
  withMarginBottom: boolean;
}

const SeoDescriptionView = ({
  content,
  faqItems,
  withMarginBottom,
}: SeoDescriptionViewProps) => {
  const { t } = useTranslation('common');

  const [isExpanded, toggleExpand] = useToggle();

  return (
    <ScSeoTopSpacing>
      <ScStaticPage>
        <ScContainer withMarginBottom={withMarginBottom}>
          <ScDescriptionContainer isExpanded={isExpanded}>
            <ScDescriptionText>
              {content && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: content,
                  }}
                />
              )}
              {faqItems && <ClientSeoFaq items={faqItems} />}
            </ScDescriptionText>
            <ScDescriptionAction isExpanded={isExpanded}>
              <Button
                onClick={toggleExpand}
                paddingX={44}
                small
                whiteTransparent
              >
                {isExpanded ? t('showLessLong') : t('showMore')}
              </Button>
            </ScDescriptionAction>
          </ScDescriptionContainer>
        </ScContainer>
      </ScStaticPage>
      {faqItems && <FaqSchema items={faqItems} />}
    </ScSeoTopSpacing>
  );
};

interface ClientSeoDescriptionProps {
  initialData: SeoContentResult | null | undefined;
  initialPath: string;
  locale: string;
}

export const ClientSeoDescription = ({
  initialData,
  initialPath,
  locale,
}: ClientSeoDescriptionProps) => {
  const { routePath, seoContent } = useSeoContent(
    locale,
    initialPath,
    initialData
  );

  const content = seoContent?.content;
  const faqItems = seoContent?.faqItems?.length ? seoContent.faqItems : null;

  if (!(content || faqItems)) {
    return null;
  }

  return (
    <SeoDescriptionView
      content={content}
      faqItems={faqItems}
      key={routePath}
      withMarginBottom={isGamePage(routePath)}
    />
  );
};
